import { useState, useEffect, useRef, createContext, useContext } from "react";
import { content } from "./content";

const LangContext = createContext({ lang: "en", setLang: () => {} });

function useLang() {
  return useContext(LangContext);
}

const COLORS = {
  bg: "#F5F2EE",
  cream: "#FAF8F5",
  sage: "#3D5A4B",
  sageLight: "#5C7A69",
  sagePale: "#EAF0EC",
  terra: "#C4725A",
  surface: "#EDE9E3",
  dark: "#1C1917",
  muted: "#7A7068",
  white: "#FFFFFF",
  border: "#DDD8D0",
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --color-bg: #F5F2EE;
    --color-cream: #FAF8F5;
    --color-sage: #3D5A4B;
    --color-sage-light: #5C7A69;
    --color-sage-pale: #EAF0EC;
    --color-terra: #C4725A;
    --color-surface: #EDE9E3;
    --color-dark: #1C1917;
    --color-muted: #7A7068;
    --color-white: #FFFFFF;
    --color-border: #DDD8D0;
    scroll-behavior: smooth;
  }

  html { font-size: 16px; }
  body { font-family: 'DM Sans', Georgia, sans-serif; background: var(--color-bg); color: var(--color-dark); overflow-x: hidden; }

  ::selection { background: rgba(61,90,75,0.18); color: var(--color-dark); }

  h1, h2 { font-family: 'Playfair Display', Georgia, serif; }

  .fade-up {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 650ms cubic-bezier(0.16,1,0.3,1), transform 650ms cubic-bezier(0.16,1,0.3,1);
  }
  .fade-up.visible { opacity: 1; transform: translateY(0); }

  @keyframes ticker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }

  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.8); opacity: 0; }
  }

  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  section { scroll-margin-top: 80px; }

  input, textarea, select {
    font-family: 'DM Sans', sans-serif;
  }

  @media (prefers-reduced-motion: reduce) {
    .fade-up { opacity: 1; transform: none; transition: none; }
    * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
  }
`;

function useFadeUp(threshold = 0.12) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, zIndex: 9999,
      height: "2px", width: `${progress}%`,
      background: `linear-gradient(to right, ${COLORS.terra}, ${COLORS.sage})`,
      transition: "width 50ms linear", pointerEvents: "none",
    }} />
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { lang, setLang } = useLang();
  const t = content[lang].nav;
  const links = t.links;
  const hrefs = ["#home","#about","#help","#approach","#telehealth","#contact"];

  return (
    <nav style={{
      position: "fixed", top: "2px", left: 0, right: 0, zIndex: 1000,
      height: scrolled ? "60px" : "72px",
      background: scrolled ? "rgba(250,248,245,0.95)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? `1px solid ${COLORS.border}` : "none",
      transition: "all 300ms ease",
      display: "flex", alignItems: "center", padding: "0 48px",
    }}>
      <a href="#home" style={{ textDecoration: "none", display: "flex", alignItems: "center", flexShrink: 0 }}>
        <img src="/logo.png" alt="Clarity Counselling Victoria" className="logo-desktop" style={{ height: "44px", width: "auto" }} />
        <img src="/logo-compact.png" alt="Clarity Counselling Victoria" className="logo-mobile" style={{ height: "40px", width: "auto", display: "none" }} />
      </a>

      <div style={{ display: "flex", gap: "36px", margin: "0 auto", alignItems: "center" }} className="desktop-nav">
        {links.map((l, i) => (
          <a key={l} href={hrefs[i]} style={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px",
            letterSpacing: "1px", color: COLORS.dark, textDecoration: "none",
            position: "relative", paddingBottom: "2px",
          }}
          onMouseEnter={e => { e.target.style.color = COLORS.sage; }}
          onMouseLeave={e => { e.target.style.color = COLORS.dark; }}>
            {l}
          </a>
        ))}
      </div>

      <div style={{ display: "flex", gap: "10px", alignItems: "center", marginRight: "20px" }} className="desktop-nav">
        {["en", "fr"].map((code) => (
          <button key={code} onClick={() => setLang(code)} style={{
            background: "none", border: "none", cursor: "pointer", padding: "4px 6px",
            fontFamily: "'DM Sans', sans-serif", fontWeight: lang === code ? 600 : 400, fontSize: "12px",
            letterSpacing: "0.5px", color: lang === code ? COLORS.sage : COLORS.muted,
          }}>{code.toUpperCase()}</button>
        ))}
      </div>

      <a href="#contact" style={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "14px",
        letterSpacing: "0.5px", color: COLORS.white,
        background: COLORS.sage, borderRadius: "8px", padding: "12px 26px",
        textDecoration: "none", transition: "all 200ms ease", flexShrink: 0,
        marginLeft: "auto",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = COLORS.sageLight; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.style.transform = "translateY(0)"; }}>
        {t.contactBtn}
      </a>

      <button onClick={() => setMenuOpen(o => !o)} style={{
        display: "none", background: "none", border: "none", cursor: "pointer",
        padding: "8px", marginLeft: "16px",
      }} className="hamburger" aria-label="Menu">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.dark} strokeWidth="1.5">
          {menuOpen
            ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
            : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
          }
        </svg>
      </button>

      {menuOpen && (
        <div style={{
          position: "fixed", top: "60px", left: 0, right: 0, bottom: 0,
          background: COLORS.cream, zIndex: 999,
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: "28px",
        }}>
          {links.map((l, i) => (
            <a key={l} href={hrefs[i]} onClick={() => setMenuOpen(false)} style={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "22px",
              color: COLORS.dark, textDecoration: "none",
            }}>{l}</a>
          ))}
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            {["en", "fr"].map((code) => (
              <button key={code} onClick={() => setLang(code)} style={{
                background: "none", border: "none", cursor: "pointer", padding: "8px 14px",
                fontFamily: "'DM Sans', sans-serif", fontWeight: lang === code ? 600 : 400, fontSize: "16px",
                letterSpacing: "0.5px", color: lang === code ? COLORS.sage : COLORS.muted,
              }}>{code.toUpperCase()}</button>
            ))}
          </div>
          <a href="#contact" onClick={() => setMenuOpen(false)} style={{
            marginTop: "16px", background: COLORS.sage, color: COLORS.white,
            padding: "16px 40px", borderRadius: "8px", textDecoration: "none",
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
          }}>{t.contactBtn}</a>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
          .logo-desktop { display: none !important; }
          .logo-mobile { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}

function Hero() {
  const { lang } = useLang();
  const t = content[lang].hero;
  return (
    <section id="home" style={{
      minHeight: "100vh", background: COLORS.bg,
      display: "flex", alignItems: "center",
      padding: "120px 48px 80px",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
        opacity: 0.025, zIndex: 0,
      }} />

      <div style={{
        maxWidth: "1200px", margin: "0 auto", width: "100%",
        display: "grid", gridTemplateColumns: "55% 45%", gap: "60px",
        alignItems: "center", position: "relative", zIndex: 1,
      }} className="hero-grid">

        <div>
          <div style={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px",
            letterSpacing: "3px", color: COLORS.terra, textTransform: "uppercase",
            marginBottom: "28px", animation: "fadeInDown 0.5s ease both",
          }}>
            &#10022; {t.kicker}
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display', serif", fontWeight: 300,
            fontSize: "clamp(40px, 5.5vw, 68px)", lineHeight: 1.15,
            color: COLORS.dark, marginBottom: "24px",
          }}>
            {t.title}
          </h1>

          <div style={{
            width: "80px", height: "3px",
            background: `linear-gradient(to right, ${COLORS.terra}, transparent)`,
            borderRadius: "2px", margin: "0 0 28px",
          }} />

          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "18px",
            lineHeight: 1.85, color: COLORS.muted, maxWidth: "460px", marginBottom: "32px",
          }}>
            {t.subtitle}
          </p>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "40px" }}>
            {t.badges.map(b => (
              <span key={b} style={{
                background: COLORS.sagePale, borderRadius: "20px", padding: "8px 16px",
                fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "12px",
                color: COLORS.sage,
              }}>{b}</span>
            ))}
          </div>

          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
            <a href="#contact" style={{
              background: COLORS.sage, color: COLORS.white,
              fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "14px",
              letterSpacing: "0.5px", padding: "16px 36px", borderRadius: "8px",
              textDecoration: "none", transition: "all 250ms ease",
              boxShadow: "0 4px 16px rgba(61,90,75,0.15)",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = COLORS.sageLight; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(61,90,75,0.25)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(61,90,75,0.15)"; }}>
              {t.ctaPrimary}
            </a>
            <a href="#about" style={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "14px",
              color: COLORS.dark, textDecoration: "none", transition: "color 200ms",
            }}
            onMouseEnter={e => e.currentTarget.style.color = COLORS.terra}
            onMouseLeave={e => e.currentTarget.style.color = COLORS.dark}>
              {t.ctaSecondary}
            </a>
          </div>
        </div>

        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "520px" }}>
          <div style={{
            position: "absolute", top: "0", right: "-20px",
            width: "280px", height: "280px", borderRadius: "50%",
            border: `2px solid rgba(196,114,90,0.2)`, pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", bottom: "20px", left: "-10px",
            width: "140px", height: "140px", borderRadius: "50%",
            border: `2px solid rgba(61,90,75,0.15)`, pointerEvents: "none",
          }} />

          <div style={{ position: "relative", width: "360px", height: "420px" }}>
            <svg viewBox="0 0 400 420" style={{ width: "100%", height: "100%", animation: "spin-slow 40s linear infinite" }}>
              <defs>
                <linearGradient id="blobGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#D4E0D8" />
                  <stop offset="100%" stopColor="#C4D4CC" />
                </linearGradient>
                <clipPath id="blobClip">
                  <path d="M200,40 C260,40 320,80 340,140 C360,200 350,270 310,320 C270,370 210,400 160,390 C110,380 70,340 50,290 C30,240 40,180 70,130 C100,80 140,40 200,40 Z" />
                </clipPath>
              </defs>
              <path d="M200,40 C260,40 320,80 340,140 C360,200 350,270 310,320 C270,370 210,400 160,390 C110,380 70,340 50,290 C30,240 40,180 70,130 C100,80 140,40 200,40 Z" fill="url(#blobGrad)" />
            </svg>

            <div style={{
              position: "absolute", inset: 0,
              clipPath: "ellipse(44% 48% at 50% 48%)",
              overflow: "hidden",
            }}>
              <img src="/sarah.jpg" alt="Sarah Cotillon" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            </div>

            <div style={{
              position: "absolute", bottom: "30px", left: "-30px",
              background: "rgba(250,248,245,0.85)", backdropFilter: "blur(20px) saturate(1.4)",
              border: "1px solid rgba(255,255,255,0.7)", borderRadius: "16px",
              padding: "20px 24px", zIndex: 10,
              animation: "float 5s ease-in-out infinite",
              boxShadow: "0 8px 32px rgba(28,25,23,0.08)",
              minWidth: "200px",
            }}>
              <div style={{ fontSize: "14px", marginBottom: "4px" }}>&#11088;&#11088;&#11088;&#11088;&#11088;</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "13px", color: COLORS.dark, marginBottom: "2px" }}>{t.trustedTitle}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.muted }}>{t.trustedSub}</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown { from { opacity:0; transform:translateY(-10px);} to { opacity:1; transform:translateY(0);} }
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function Ticker() {
  const { lang } = useLang();
  const items = content[lang].ticker;
  return (
    <div style={{ background: COLORS.sage, height: "52px", overflow: "hidden", display: "flex", alignItems: "center" }}>
      <div style={{ display: "flex", animation: "ticker 35s linear infinite", whiteSpace: "nowrap" }}>
        {[...items,...items].map((item, i) => (
          <span key={i} style={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "11px",
            letterSpacing: "3px", textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            padding: "0 24px",
          }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function HowIHelp() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].howIHelp;

  return (
    <section id="help" style={{ background: COLORS.cream, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto", textAlign: "center" }}>
        <div ref={ref} className="fade-up">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.muted, textTransform: "uppercase", marginBottom: "20px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(28px, 4vw, 44px)", color: COLORS.dark, lineHeight: 1.2, marginBottom: "24px" }}>
            {t.title}
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "17px", lineHeight: 1.85, color: COLORS.muted, marginBottom: "48px", maxWidth: "680px", margin: "0 auto 48px" }}>
            {t.intro}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px", textAlign: "left", marginBottom: "32px" }} className="focus-grid">
            {t.focusAreas.map(area => (
              <div key={area.title} style={{
                background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: "16px",
                padding: "32px 28px", boxShadow: "0 4px 20px rgba(28,25,23,0.05)",
              }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: "19px", color: COLORS.sage, marginBottom: "12px" }}>{area.title}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "14px", lineHeight: 1.7, color: COLORS.muted }}>{area.body}</p>
              </div>
            ))}
          </div>

          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontStyle: "italic", fontSize: "15px", color: COLORS.muted }}>
            {t.footnote}
          </p>
        </div>
      </div>
      <style>{`@media (max-width: 768px) { .focus-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function AboutSarah() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].about;
  return (
    <section id="about" style={{ background: COLORS.bg, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "45% 55%", gap: "80px", alignItems: "center" }} className="about-grid">

        <div style={{ position: "relative" }}>
          <div style={{
            width: "100%", aspectRatio: "4/5",
            borderRadius: "60% 40% 55% 45% / 45% 55% 45% 55%",
            position: "relative", overflow: "hidden",
          }}>
            <img src="/sarah.jpg" alt="Sarah Cotillon" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
          </div>
          <div style={{
            position: "absolute", bottom: "20px", right: "-20px",
            background: "rgba(250,248,245,0.9)", backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.65)", borderRadius: "12px",
            padding: "16px 20px", maxWidth: "200px",
            boxShadow: "0 8px 24px rgba(28,25,23,0.08)",
          }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", color: COLORS.sage, marginBottom: "4px" }}>{t.credentialLine1}</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "10px", color: COLORS.muted }}>{t.credentialLine2}</div>
          </div>
        </div>

        <div ref={ref} className="fade-up" style={{ paddingLeft: "20px" }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.terra, textTransform: "uppercase", marginBottom: "20px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(28px, 3.5vw, 40px)", color: COLORS.dark, lineHeight: 1.2, marginBottom: "28px" }}>
            {t.title}
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "16px", lineHeight: 1.85, color: COLORS.dark, marginBottom: "24px" }}>
            {t.p1}
          </p>

          <div style={{ width: "60px", height: "2px", background: COLORS.sage, margin: "24px 0" }} />

          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "16px", lineHeight: 1.85, color: COLORS.dark, marginBottom: "36px" }}>
            {t.p2}
          </p>

          <a href="#contact" style={{
            display: "inline-block", marginTop: "28px",
            fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "14px",
            color: COLORS.dark, textDecoration: "none",
            borderBottom: `1px solid ${COLORS.sage}`, paddingBottom: "2px",
            transition: "color 200ms",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.sage}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.dark}>
            {t.link}
          </a>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .about-grid > div:first-child { max-width: 220px; margin: 0 auto; border-radius: 50% !important; }
        }
      `}</style>
    </section>
  );
}

function ApproachCard({ card }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "rgba(255,255,255,0.11)" : "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "16px", padding: "36px 28px",
        transition: "all 300ms ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
      }}>
      <div style={{ marginBottom: "20px" }}>{card.icon}</div>
      <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "18px", color: COLORS.white, marginBottom: "12px" }}>{card.title}</h3>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "20px" }}>{card.body}</p>
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px" }}>{card.tag}</span>
    </div>
  );
}

function TelehealthCard({ card }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: COLORS.white, border: `1px solid ${hovered ? COLORS.sage : "rgba(61,90,75,0.18)"}`,
        borderRadius: "12px", padding: "24px",
        transition: "all 200ms ease", transform: hovered ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered ? "0 8px 24px rgba(61,90,75,0.1)" : "none",
        display: "flex", gap: "16px", alignItems: "flex-start",
      }}>
      <span style={{ fontSize: "24px" }}>{card.icon}</span>
      <div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "15px", color: COLORS.dark, marginBottom: "4px" }}>{card.title}</div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "13px", color: COLORS.muted }}>{card.sub}</div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.sage, marginTop: "2px" }}>{card.detail}</div>
      </div>
    </div>
  );
}

function Approach() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].approach;
  const icons = [
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"><circle cx="18" cy="18" r="12"/><path d="M6 18 Q10 10 18 18 Q26 26 30 18"/><circle cx="18" cy="18" r="3" fill="rgba(255,255,255,0.5)"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"><circle cx="13" cy="15" r="6"/><circle cx="23" cy="15" r="6"/><path d="M13 21 Q18 28 23 21"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"><path d="M18 4 L18 20 M12 14 L18 20 L24 14"/><path d="M8 24 Q8 32 18 32 Q28 32 28 24"/><path d="M4 28 Q6 24 8 24"/><path d="M32 28 Q30 24 28 24"/></svg>,
  ];
  const cards = t.cards.map((card, i) => ({ ...card, icon: icons[i] }));

  return (
    <section id="approach" style={{ background: COLORS.sage, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div ref={ref} className="fade-up" style={{ textAlign: "center", marginBottom: "60px" }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: "rgba(196,114,90,0.8)", textTransform: "uppercase", marginBottom: "20px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, fontSize: "clamp(28px, 4vw, 44px)", color: COLORS.white, lineHeight: 1.2 }}>
            {t.titleLine1}<br />{t.titleLine2}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }} className="approach-grid">
          {cards.map((card, i) => <ApproachCard key={i} card={card} />)}
        </div>

        <p style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "14px", color: "rgba(255,255,255,0.55)", marginTop: "40px", maxWidth: "680px", margin: "40px auto 0" }}>
          {t.footnote}
        </p>
      </div>

      <style>{`@media (max-width: 768px) { .approach-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function Testimonials() {
  const [active, setActive] = useState(0);
  const { lang } = useLang();
  const t = content[lang].testimonials;
  const testimonials = t.items;
  useEffect(() => { setActive(0); }, [lang]);
  const ref = useFadeUp();

  return (
    <section style={{ background: COLORS.cream, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div ref={ref} className="fade-up" style={{ textAlign: "center", marginBottom: "60px" }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.muted, textTransform: "uppercase", marginBottom: "20px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(26px, 3.5vw, 40px)", color: COLORS.dark }}>
            {t.title}
          </h2>
        </div>

        <div style={{
          background: COLORS.white, borderRadius: "16px", padding: "48px 52px",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 4px 24px rgba(28,25,23,0.06)",
          position: "relative", overflow: "hidden", minHeight: "220px",
        }}>
          <div style={{
            position: "absolute", top: "-20px", left: "20px",
            fontFamily: "'Playfair Display', serif", fontWeight: 300,
            fontSize: "120px", color: "rgba(61,90,75,0.1)", lineHeight: 1, pointerEvents: "none",
          }}>"</div>

          <p style={{
            fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300,
            fontSize: "clamp(16px, 2vw, 20px)", color: COLORS.dark, lineHeight: 1.7,
            marginBottom: "28px", position: "relative", zIndex: 1,
          }}>
            {testimonials[active].quote}
          </p>

          <div>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "13px", color: COLORS.sage }}>&mdash; {testimonials[active].name}</span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.muted, marginLeft: "12px" }}>{testimonials[active].loc} &middot; {testimonials[active].type}</span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", marginTop: "32px" }}>
          <button onClick={() => setActive(a => (a - 1 + testimonials.length) % testimonials.length)} style={{
            width: "40px", height: "40px", borderRadius: "50%", border: `1px solid ${COLORS.sage}`,
            background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 200ms",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.querySelector("svg").style.stroke = COLORS.white; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.querySelector("svg").style.stroke = COLORS.sage; }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><path d="M10 4L6 8L10 12"/></svg>
          </button>

          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} style={{
              width: i === active ? "24px" : "8px", height: "8px",
              borderRadius: "4px", border: "none", cursor: "pointer",
              background: i === active ? COLORS.sage : COLORS.border,
              transition: "all 300ms ease",
            }} />
          ))}

          <button onClick={() => setActive(a => (a + 1) % testimonials.length)} style={{
            width: "40px", height: "40px", borderRadius: "50%", border: `1px solid ${COLORS.sage}`,
            background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 200ms",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.querySelector("svg").style.stroke = COLORS.white; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.querySelector("svg").style.stroke = COLORS.sage; }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><path d="M6 4L10 8L6 12"/></svg>
          </button>
        </div>
      </div>
    </section>
  );
}

function Telehealth() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].telehealth;
  const cards = t.cards;
  return (
    <section id="telehealth" style={{ background: COLORS.bg, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "50% 50%", gap: "60px", alignItems: "center" }} className="tele-grid">
        <div ref={ref} className="fade-up">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.muted, textTransform: "uppercase", marginBottom: "20px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(28px, 3.5vw, 40px)", color: COLORS.dark, marginBottom: "24px" }}>
            {t.title}
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "16px", lineHeight: 1.85, color: COLORS.muted, marginBottom: "32px" }}>
            {t.intro}
          </p>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "16px", marginBottom: "40px" }}>
            {t.list.map(item => (
              <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "12px", fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px", color: COLORS.dark }}>
                <span style={{ color: COLORS.sage, marginTop: "2px", flexShrink: 0 }}>✓</span>
                {item}
              </li>
            ))}
          </ul>
          <a href="#contact" style={{
            display: "inline-block", background: COLORS.sage, color: COLORS.white,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "14px",
            padding: "14px 32px", borderRadius: "8px", textDecoration: "none",
            transition: "all 200ms ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.sageLight; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.style.transform = "translateY(0)"; }}>
            {t.cta}
          </a>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {cards.map(card => <TelehealthCard key={card.title} card={card} />)}
        </div>
      </div>
      <style>{`@media (max-width: 768px) { .tele-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function Process() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].process;
  const icons = [
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><rect x="4" y="8" width="28" height="20" rx="3"/><path d="M4 12L18 22L32 12"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><circle cx="13" cy="14" r="6"/><circle cx="23" cy="14" r="6"/><path d="M4 32 Q4 24 13 24"/><path d="M32 32 Q32 24 23 24"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><path d="M4 28 Q10 20 18 18 Q26 16 32 8"/><path d="M26 8 L32 8 L32 14"/></svg>,
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke={COLORS.sage} strokeWidth="1.5"><polygon points="18,4 22,14 33,14 24,21 27,32 18,25 9,32 12,21 3,14 14,14"/></svg>,
  ];
  const steps = t.steps.map((step, i) => ({ ...step, icon: icons[i] }));

  return (
    <section style={{ background: COLORS.cream, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div ref={ref} className="fade-up" style={{ textAlign: "center", marginBottom: "60px" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(28px, 3.5vw, 40px)", color: COLORS.dark, marginBottom: "16px" }}>{t.title}</h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "17px", color: COLORS.muted }}>{t.subtitle}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0", position: "relative" }} className="process-grid">
          {steps.map((step, i) => (
            <div key={i} style={{ textAlign: "center", padding: "0 24px", position: "relative" }}>
              {i < steps.length - 1 && (
                <div style={{
                  position: "absolute", top: "52px", left: "50%", right: "-50%",
                  height: "1px", borderTop: `2px dashed rgba(61,90,75,0.25)`,
                  zIndex: 0,
                }} className="step-connector" />
              )}
              <div style={{ fontSize: "48px", fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300, color: "rgba(61,90,75,0.18)", lineHeight: 1, marginBottom: "16px" }}>{step.num}</div>
              <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center" }}>{step.icon}</div>
              <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "16px", color: COLORS.dark, marginBottom: "10px" }}>{step.title}</h3>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "14px", color: COLORS.muted, lineHeight: 1.7 }}>{step.body}</p>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .process-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .step-connector { display: none !important; }
        }
      `}</style>
    </section>
  );
}

function QuoteSection() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].quote;
  return (
    <section style={{ background: COLORS.sage, padding: "100px 40px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-60px", left: "-60px", width: "300px", height: "300px", borderRadius: "50%", background: "rgba(92,122,105,0.15)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-80px", right: "-40px", width: "250px", height: "250px", borderRadius: "50%", background: "rgba(92,122,105,0.1)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "20px", left: "40px", fontSize: "120px", fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.08)", pointerEvents: "none", lineHeight: 1 }}>"</div>

      <div ref={ref} className="fade-up" style={{ position: "relative", zIndex: 1, maxWidth: "720px", margin: "0 auto" }}>
        <p style={{
          fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300,
          fontSize: "clamp(20px, 2.5vw, 28px)", color: COLORS.white, lineHeight: 1.6, marginBottom: "32px",
        }}>
          "{t.text}"
        </p>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "14px", color: "rgba(196,114,90,0.85)", letterSpacing: "1px" }}>
          &mdash; Sarah Cotillon &nbsp;&middot;&nbsp; Clarity Counselling Vic
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].faq;
  const faqs = t.items;
  useEffect(() => { setOpen(null); }, [lang]);

  return (
    <section style={{ background: COLORS.bg, padding: "100px 48px" }}>
      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        <div ref={ref} className="fade-up" style={{ textAlign: "center", marginBottom: "56px" }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.muted, textTransform: "uppercase", marginBottom: "16px" }}>{t.kicker}</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(26px, 3.5vw, 38px)", color: COLORS.dark }}>{t.title}</h2>
        </div>

        {faqs.map((faq, i) => (
          <div key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              style={{
                width: "100%", textAlign: "left", background: "none", border: "none",
                padding: "22px 0", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                backgroundColor: open === i ? "rgba(234,240,236,0.4)" : "transparent",
                paddingLeft: open === i ? "16px" : "0",
                paddingRight: open === i ? "16px" : "0",
                transition: "all 200ms ease",
              }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "16px", color: COLORS.dark }}>{faq.q}</span>
              <span style={{
                width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center",
                color: COLORS.sage, fontSize: "20px", fontWeight: 300, flexShrink: 0,
                transform: open === i ? "rotate(45deg)" : "rotate(0)", transition: "transform 200ms ease",
              }}>+</span>
            </button>
            <div style={{
              maxHeight: open === i ? "300px" : "0",
              overflow: "hidden", transition: "max-height 300ms ease",
            }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px", color: COLORS.muted, lineHeight: 1.8, paddingBottom: "22px", paddingLeft: open === i ? "16px" : "0", paddingRight: open === i ? "16px" : "0" }}>{faq.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CTASection() {
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].cta;
  return (
    <section style={{ background: COLORS.cream, padding: "120px 40px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-100px", right: "-100px", width: "400px", height: "400px", borderRadius: "60% 40% 55% 45% / 45% 55% 45% 55%", background: COLORS.sagePale, opacity: 0.5, pointerEvents: "none" }} />

      <div ref={ref} className="fade-up" style={{ position: "relative", zIndex: 1 }}>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.terra, textTransform: "uppercase", marginBottom: "24px" }}>{t.kicker}</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, fontSize: "clamp(32px, 4.5vw, 52px)", color: COLORS.dark, marginBottom: "24px" }}>
          {t.title}
        </h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "18px", color: COLORS.muted, maxWidth: "580px", margin: "0 auto 48px", lineHeight: 1.8 }}>
          {t.body}
        </p>

        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap", marginBottom: "48px" }}>
          <a href="#contact" style={{
            background: COLORS.sage, color: COLORS.white,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "14px",
            padding: "18px 48px", borderRadius: "10px", textDecoration: "none",
            transition: "all 250ms ease", boxShadow: "0 4px 16px rgba(61,90,75,0.15)",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.sageLight; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(61,90,75,0.3)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(61,90,75,0.15)"; }}>
            {t.primaryBtn}
          </a>
          <a href="tel:+61488988234" style={{
            border: `1px solid ${COLORS.sage}`, color: COLORS.sage,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "14px",
            padding: "18px 48px", borderRadius: "10px", textDecoration: "none",
            transition: "all 250ms ease", background: "transparent",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = COLORS.sage; e.currentTarget.style.color = COLORS.white; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = COLORS.sage; }}>
            {t.phoneBtn}
          </a>
        </div>

        <div style={{ display: "flex", gap: "32px", justifyContent: "center", flexWrap: "wrap" }}>
          {t.badges.map(b => (
            <span key={b} style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "12px", color: COLORS.sage }}>{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

const FORMSPREE_ENDPOINT = "https://formspree.io/f/REPLACE_ME";

// status: "idle" | "sending" | "sent" | "error"
function useContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "", lang: "English" });
  const [status, setStatus] = useState("idle");
  const [errorDetail, setErrorDetail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (e.target._gotcha.value) { setStatus("sent"); return; } // honeypot tripped: fake success, never reveal the block to bot or human
    setStatus("sending");
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          topic: form.topic, message: form.message, language: form.lang,
          _subject: "New enquiry from claritycounsellingvic.com",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.errors?.map(e => e.message).join(", ") || "Formspree submission failed");
      }
      setStatus("sent");
    } catch (err) {
      console.error("Contact form submission failed:", err);
      setErrorDetail(err.message || "");
      setStatus("error");
    }
  };

  return { form, setForm, status, errorDetail, handleSubmit };
}

function Contact() {
  const { form, setForm, status, errorDetail, handleSubmit } = useContactForm();
  const ref = useFadeUp();
  const { lang } = useLang();
  const t = content[lang].contactForm;

  return (
    <section id="contact" style={{ background: COLORS.bg, padding: "100px 48px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "55% 45%", gap: "60px" }} className="contact-grid">

        <div ref={ref} className="fade-up">
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: "clamp(26px, 3vw, 36px)", color: COLORS.dark, marginBottom: "12px" }}>{t.title}</h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "16px", color: COLORS.muted, marginBottom: "36px" }}>
            {t.intro}
          </p>

          {status === "sent" ? (
            <div style={{
              background: COLORS.sagePale, border: `1px solid rgba(61,90,75,0.3)`,
              borderRadius: "12px", padding: "32px", textAlign: "center",
            }}>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>✓</div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "16px", color: COLORS.sage }}>{t.successMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <input type="text" name="_gotcha" tabIndex="-1" autoComplete="off" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true" />
              {[
                { key: "name", label: t.nameLabel, type: "text", required: true },
                { key: "email", label: t.emailLabel, type: "email", required: true },
                { key: "phone", label: t.phoneLabel, type: "tel", required: false },
              ].map(field => (
                <div key={field.key} style={{ position: "relative" }}>
                  <input
                    type={field.type}
                    required={field.required}
                    placeholder={field.label}
                    value={form[field.key]}
                    onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                    style={{
                      width: "100%", padding: "16px 18px",
                      border: `1px solid ${COLORS.border}`, borderRadius: "10px",
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px",
                      color: COLORS.dark, background: COLORS.white, outline: "none",
                      transition: "border 200ms, box-shadow 200ms",
                    }}
                    onFocus={e => { e.target.style.border = `2px solid ${COLORS.sage}`; e.target.style.boxShadow = `0 0 0 4px rgba(61,90,75,0.08)`; }}
                    onBlur={e => { e.target.style.border = `1px solid ${COLORS.border}`; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              ))}

              <select
                value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                style={{
                  width: "100%", padding: "16px 18px",
                  border: `1px solid ${COLORS.border}`, borderRadius: "10px",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px",
                  color: form.topic ? COLORS.dark : COLORS.muted, background: COLORS.white, outline: "none",
                }}>
                <option value="">{t.topicPlaceholder}</option>
                {t.topicOptions.map(o => <option key={o}>{o}</option>)}
              </select>

              <textarea
                rows="4"
                placeholder={t.messagePlaceholder}
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                style={{
                  width: "100%", padding: "16px 18px",
                  border: `1px solid ${COLORS.border}`, borderRadius: "10px",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "15px",
                  color: COLORS.dark, background: COLORS.white, outline: "none",
                  resize: "vertical", transition: "border 200ms, box-shadow 200ms",
                }}
                onFocus={e => { e.target.style.border = `2px solid ${COLORS.sage}`; e.target.style.boxShadow = `0 0 0 4px rgba(61,90,75,0.08)`; }}
                onBlur={e => { e.target.style.border = `1px solid ${COLORS.border}`; e.target.style.boxShadow = "none"; }}
              />

              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", color: COLORS.muted }}>{t.preferredLanguageLabel}</span>
                {t.langOptions.map(opt => (
                  <button key={opt.value} type="button" onClick={() => setForm(f => ({ ...f, lang: opt.value }))} style={{
                    padding: "6px 18px", borderRadius: "20px", cursor: "pointer",
                    border: `1px solid ${form.lang === opt.value ? COLORS.sage : COLORS.border}`,
                    background: form.lang === opt.value ? COLORS.sagePale : "transparent",
                    color: form.lang === opt.value ? COLORS.sage : COLORS.muted,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px",
                    transition: "all 200ms",
                  }}>{opt.label}</button>
                ))}
              </div>

              {status === "error" && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", color: COLORS.terra }}>
                  {errorDetail && errorDetail !== "Formspree submission failed"
                    ? `${t.checkErrorPrefix} ${errorDetail}`
                    : t.genericError}
                </p>
              )}

              <button type="submit" disabled={status === "sending"} style={{
                width: "100%", background: COLORS.sage, color: COLORS.white, border: "none",
                padding: "18px", borderRadius: "10px", cursor: status === "sending" ? "default" : "pointer",
                fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "15px",
                transition: "all 200ms ease", opacity: status === "sending" ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (status !== "sending") e.currentTarget.style.background = COLORS.sageLight; }}
              onMouseLeave={e => { e.currentTarget.style.background = COLORS.sage; }}>
                {status === "sending" ? t.sendingBtn : t.sendBtn}
              </button>

              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.muted, textAlign: "center" }}>
                {t.secureNote}
              </p>
            </form>
          )}
        </div>

        <div>
          <iframe
            title="Clarity Counselling Victoria location"
            src="https://maps.google.com/maps?q=240+Albert+Road,+South+Melbourne+VIC+3205,+Australia&t=&z=15&ie=UTF8&iwloc=&output=embed"
            width="100%" height="320"
            style={{ border: 0, borderRadius: "16px", marginBottom: "32px", display: "block" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {[
              { icon: "📍", label: t.infoAddress, sub: t.infoAddressSub },
              { icon: "📞", label: t.infoPhone, sub: null },
              { icon: "🕐", label: t.infoHours, sub: t.infoHoursSub },
            ].map(info => (
              <div key={info.label} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <span style={{ fontSize: "18px" }}>{info.icon}</span>
                <div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "14px", color: COLORS.dark }}>{info.label}</div>
                  {info.sub && <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.muted }}>{info.sub}</div>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "24px", padding: "16px 20px", background: COLORS.sagePale, borderRadius: "10px", border: `1px solid rgba(61,90,75,0.2)` }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", color: COLORS.sage }}>{t.telehealthNote}</p>
          </div>

          <p style={{ marginTop: "16px", fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: COLORS.muted }}>{t.parkingNote}</p>
        </div>
      </div>
      <style>{`@media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function Footer() {
  const { lang } = useLang();
  const t = content[lang].footer;
  return (
    <footer style={{ background: COLORS.dark, padding: "72px 48px 40px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "48px", marginBottom: "48px" }} className="footer-grid">

          <div>
            <div style={{ marginBottom: "16px" }}>
              <img src="/logo-headway.jpg" alt="Clarity Counselling Victoria" style={{ height: "72px", width: "auto", borderRadius: "50%" }} />
            </div>
            <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300, fontSize: "13px", color: "rgba(255,255,255,0.35)", marginBottom: "24px" }}>
              {t.tagline}
            </p>
          </div>

          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.sagePale, textTransform: "uppercase", marginBottom: "20px" }}>{t.navigateHeading}</div>
            {t.navLinks.map((l, i) => (
              <a key={l} href={["#home","#about","#help","#approach","#telehealth","#contact"][i]} style={{
                display: "block", marginBottom: "10px",
                fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "14px",
                color: "rgba(255,255,255,0.5)", textDecoration: "none", transition: "color 200ms",
              }}
              onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.9)"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}>{l}</a>
            ))}
          </div>

          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "11px", letterSpacing: "3px", color: COLORS.sagePale, textTransform: "uppercase", marginBottom: "20px" }}>{t.contactHeading}</div>
            {t.contactLines.map(l => (
              <p key={l} style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "13px", color: "rgba(255,255,255,0.5)", marginBottom: "8px" }}>{l}</p>
            ))}
            <div style={{ marginTop: "20px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {t.badges.map(b => (
                <span key={b} style={{
                  padding: "4px 12px", border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "20px", fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
                  fontSize: "10px", color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px",
                }}>{b}</span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "32px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.3)" }}>
            {t.copyright}
          </p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.3)" }}>
            {t.address}
          </p>
        </div>
      </div>
      <style>{`@media (max-width: 768px) { .footer-grid { grid-template-columns: 1fr !important; } }`}</style>
    </footer>
  );
}

function FAB() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return visible ? (
    <a href="tel:+61488988234" style={{
      position: "fixed", bottom: "20px", right: "20px", zIndex: 9999,
      width: "56px", height: "56px", borderRadius: "50%",
      background: COLORS.sage, color: COLORS.white,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 8px 24px rgba(61,90,75,0.4)",
      textDecoration: "none", animation: "float 3s ease-in-out infinite",
    }} className="fab-mobile">
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="white" strokeWidth="1.5">
        <path d="M4 4C4 4 6 2 8 4L10 7C10 7 11 9 9 11L8 12C8 12 11 15 14 14L15 13C17 11 19 12 19 12L22 14C24 16 22 18 22 18C22 18 20 22 14 20C8 18 4 14 2 8C0 2 4 4 4 4Z" transform="scale(0.85) translate(1.5,1.5)"/>
      </svg>
      <style>{`
        @media (min-width: 769px) { .fab-mobile { display: none !important; } }
      `}</style>
    </a>
  ) : null;
}

function SchemaOrg() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Clarity Counselling Vic",
    description: "Counsellor Sarah Cotillon offers EMDR, couples therapy (Gottman Method), and individual counselling in South Melbourne & online. Bilingual English/French. PACFA registered.",
    address: { "@type": "PostalAddress", streetAddress: "240 Albert Road", addressLocality: "South Melbourne", addressRegion: "VIC", postalCode: "3205", addressCountry: "AU" },
    telephone: "+61488988234",
    openingHours: ["Mo-Sa 07:00-21:00"],
    availableLanguage: ["English", "French"],
    url: "https://claritycounsellingvic.com",
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function getStoredLang() {
  try {
    const stored = window.localStorage.getItem("clarity-lang");
    return stored === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

export default function App() {
  const [lang, setLangState] = useState(getStoredLang);

  const setLang = (next) => {
    setLangState(next);
    try {
      window.localStorage.setItem("clarity-lang", next);
    } catch {
      // localStorage unavailable — in-memory state still works
    }
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <style>{globalStyles}</style>
      <SchemaOrg />
      <ScrollProgressBar />
      <Nav />
      <main>
        <Hero />
        <Ticker />
        <HowIHelp />
        <AboutSarah />
        <Approach />
        <Testimonials />
        <Telehealth />
        <Process />
        <QuoteSection />
        <FAQ />
        <CTASection />
        <Contact />
      </main>
      <Footer />
      <FAB />
    </LangContext.Provider>
  );
}
